import { adicionarLogo } from "../../services/pdf-logo";
import jsPDF from "jspdf";
import { formatarParaReais } from "../../services/utilitarios";
import { DadosFatura, ParteFatura, PixFatura, SITUACAO_EM_ATRASO, SITUACAO_PAGA, totalDaFatura, valorEmAbertoDaFatura } from "./fatura-dados";

const paleta = {
    texto: "#1A202C",
    apagado: "#718096",
    destaque: "#1F467E",
    borda: "#CBD5E0",
    fundo: "#F7FAFC",
    atraso: "#B00020",
    pago: "#2F855A",
};

const margem = 15;
const raio = 1.5;

const textoData = (valor?: string | Date | null): string => {
    if (!valor) return "";
    const data = valor instanceof Date ? valor : new Date(valor);
    return isNaN(data.getTime()) ? "" : data.toLocaleDateString("pt-BR");
};

const corDaSituacao = (situacao: string) => {
    if (situacao === SITUACAO_PAGA) return paleta.pago;
    if (situacao === SITUACAO_EM_ATRASO) return paleta.atraso;
    return paleta.destaque;
};

const linhasDaParte = (parte: ParteFatura): string[] => {
    const contato = [parte.documento ? `CPF/CNPJ: ${parte.documento}` : "", parte.email, parte.telefone].filter(Boolean).join("  •  ");
    return [contato, parte.endereco ?? ""].filter(Boolean);
};

const tituloDaCaixa = (pdf: jsPDF, x: number, y: number, titulo: string) => {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.apagado);
    pdf.text(titulo.toUpperCase(), x, y);
};

const caixa = (pdf: jsPDF, y: number, altura: number) => {
    const largura = pdf.internal.pageSize.width - margem * 2;
    pdf.setDrawColor(paleta.borda);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(margem, y, largura, altura, raio, raio, "S");
};

const cabecalho = (pdf: jsPDF, dados: DadosFatura, logo: HTMLImageElement | null): number => {
    const larguraPagina = pdf.internal.pageSize.width;
    const larguraTexto = larguraPagina - margem * 2;
    let xEmissor = margem;
    if (logo) {
        const desenhada = adicionarLogo(pdf, logo, { x: margem, y: 12, largura: 34, altura: 18 });
        xEmissor = margem + desenhada.largura + 6;
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(paleta.texto);
    pdf.text(dados.emissor.nome, xEmissor, 17, { maxWidth: larguraTexto * 0.55 });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(paleta.apagado);
    let y = 23;
    if (dados.emissor.documento) {
        pdf.text(`CNPJ/CPF: ${dados.emissor.documento}`, xEmissor, y);
        y += 4.5;
    }
    if (dados.emissor.endereco) {
        const linhas = pdf.splitTextToSize(dados.emissor.endereco, larguraTexto * 0.55);
        pdf.text(linhas, xEmissor, y);
        y += linhas.length * 4.5;
    }
    const contato = [dados.emissor.telefone, dados.emissor.email].filter(Boolean).join("  •  ");
    if (contato) {
        pdf.text(contato, xEmissor, y);
        y += 4.5;
    }

    const xDireita = larguraPagina - margem;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(paleta.texto);
    pdf.text(`FATURA Nº ${dados.numero}`, xDireita, 18, { align: "right" });

    pdf.setFontSize(9);
    pdf.setTextColor(corDaSituacao(dados.situacao));
    pdf.text(dados.situacao, xDireita, 25, { align: "right" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(paleta.apagado);
    pdf.text(`Emissão: ${textoData(dados.emissao)}`, xDireita, 31, { align: "right" });

    const yLinha = Math.max(y, 36) + 3;
    pdf.setDrawColor(paleta.texto);
    pdf.setLineWidth(0.9);
    pdf.line(margem, yLinha, larguraPagina - margem, yLinha);
    return yLinha + 7;
};

const blocoCliente = (pdf: jsPDF, y: number, cliente: ParteFatura | null): number => {
    const larguraTexto = pdf.internal.pageSize.width - margem * 2 - 8;
    const linhas = cliente
        ? linhasDaParte(cliente).reduce<string[]>((todas, l) => todas.concat(pdf.splitTextToSize(l, larguraTexto) as string[]), [])
        : [];
    const altura = 12 + (cliente ? 6 : 0) + linhas.length * 4.5 + 2;
    caixa(pdf, y, altura);
    tituloDaCaixa(pdf, margem + 4, y + 6, "Cliente");

    let yTexto = y + 12;
    if (cliente) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10.5);
        pdf.setTextColor(paleta.texto);
        pdf.text(cliente.nome, margem + 4, yTexto);
        yTexto += 6;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.5);
        pdf.setTextColor(paleta.apagado);
        linhas.forEach(l => {
            pdf.text(l, margem + 4, yTexto);
            yTexto += 4.5;
        });
    } else {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(paleta.apagado);
        pdf.text("Não informado", margem + 4, yTexto);
    }
    return y + altura + 5;
};

const blocoItens = (pdf: jsPDF, y: number, dados: DadosFatura): number => {
    const larguraPagina = pdf.internal.pageSize.width;
    const xFim = larguraPagina - margem - 4;
    const colunas = {
        numero: margem + 5,
        descricao: margem + 13,
        unidade: xFim - 78,
        quantidade: xFim - 60,
        unitario: xFim - 30,
        total: xFim,
    };
    const larguraDescricao = colunas.unidade - colunas.descricao - 6;

    const linhasPorItem = dados.itens.map(item => ({
        item,
        descricao: pdf.splitTextToSize(item.descricao, larguraDescricao) as string[],
        detalhe: item.detalhe ? (pdf.splitTextToSize(item.detalhe, larguraDescricao) as string[]) : [],
    }));
    const alturaItens = linhasPorItem.reduce((soma, l) => soma + Math.max(l.descricao.length * 4.5 + l.detalhe.length * 3.8, 6) + 3, 0);
    const altura = 12 + 8 + alturaItens + 2;
    caixa(pdf, y, altura);
    tituloDaCaixa(pdf, margem + 4, y + 6, "Itens da fatura");

    let yTexto = y + 13;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.apagado);
    pdf.text("#", colunas.numero, yTexto);
    pdf.text("Descrição", colunas.descricao, yTexto);
    pdf.text("Un.", colunas.unidade, yTexto, { align: "right" });
    pdf.text("Qtde.", colunas.quantidade, yTexto, { align: "right" });
    pdf.text("Valor unit.", colunas.unitario, yTexto, { align: "right" });
    pdf.text("Total", colunas.total, yTexto, { align: "right" });
    yTexto += 2;
    pdf.setDrawColor(paleta.borda);
    pdf.setLineWidth(0.2);
    pdf.line(margem + 4, yTexto, larguraPagina - margem - 4, yTexto);
    yTexto += 5;

    linhasPorItem.forEach((linha, indice) => {
        const yLinha = yTexto;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(paleta.destaque);
        pdf.text(String(indice + 1), colunas.numero, yLinha);
        pdf.setTextColor(paleta.texto);
        pdf.text(linha.descricao, colunas.descricao, yLinha);
        pdf.text(linha.item.unidade, colunas.unidade, yLinha, { align: "right" });
        pdf.text(String(linha.item.quantidade), colunas.quantidade, yLinha, { align: "right" });
        pdf.text(formatarParaReais(linha.item.valorUnitario), colunas.unitario, yLinha, { align: "right" });
        pdf.setFont("helvetica", "bold");
        pdf.text(formatarParaReais(linha.item.quantidade * linha.item.valorUnitario), colunas.total, yLinha, { align: "right" });
        let yDetalhe = yLinha + linha.descricao.length * 4.5;
        if (linha.detalhe.length) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(7.5);
            pdf.setTextColor(paleta.apagado);
            pdf.text(linha.detalhe, colunas.descricao, yDetalhe - 0.5);
            yDetalhe += linha.detalhe.length * 3.8;
        }
        yTexto = Math.max(yDetalhe, yLinha + 6) + 3;
    });
    return y + altura + 5;
};

const blocoParcelas = (pdf: jsPDF, y: number, dados: DadosFatura): number => {
    const larguraPagina = pdf.internal.pageSize.width;
    const xFim = larguraPagina - margem - 4;
    const colunas = {
        parcela: margem + 8,
        forma: margem + 30,
        vencimento: margem + 88,
        observacoes: margem + 115,
        valor: xFim,
    };
    const larguraObservacoes = colunas.valor - colunas.observacoes - 24;
    const linhas = dados.parcelas.map(p => ({
        parcela: p,
        observacoes: p.observacoes ? (pdf.splitTextToSize(p.observacoes, larguraObservacoes) as string[]) : [],
    }));
    const alturaParcelas = linhas.reduce((soma, l) => soma + Math.max(l.observacoes.length, 1) * 4.5 + 2, 0);
    const altura = 12 + 8 + alturaParcelas + 9 + 9;
    caixa(pdf, y, altura);
    tituloDaCaixa(pdf, margem + 4, y + 6, "Parcelas / Pagamento");

    let yTexto = y + 13;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.apagado);
    pdf.text("Parcela", colunas.parcela, yTexto);
    pdf.text("Forma de pagamento", colunas.forma, yTexto);
    pdf.text("Vencimento", colunas.vencimento, yTexto);
    pdf.text("Observações", colunas.observacoes, yTexto);
    pdf.text("Valor", colunas.valor, yTexto, { align: "right" });
    yTexto += 2;
    pdf.setDrawColor(paleta.borda);
    pdf.setLineWidth(0.2);
    pdf.line(margem + 4, yTexto, larguraPagina - margem - 4, yTexto);
    yTexto += 5;

    linhas.forEach(linha => {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(paleta.texto);
        pdf.text(linha.parcela.parcela, colunas.parcela, yTexto);
        pdf.text(linha.parcela.forma, colunas.forma, yTexto);
        pdf.text(textoData(linha.parcela.vencimento), colunas.vencimento, yTexto);
        if (linha.observacoes.length) {
            pdf.setTextColor(paleta.apagado);
            pdf.text(linha.observacoes, colunas.observacoes, yTexto);
            pdf.setTextColor(paleta.texto);
        }
        pdf.setFont("helvetica", "bold");
        pdf.text(formatarParaReais(linha.parcela.valor), colunas.valor, yTexto, { align: "right" });
        yTexto += Math.max(linha.observacoes.length, 1) * 4.5 + 2;
    });

    pdf.setDrawColor(paleta.borda);
    pdf.line(margem + 4, yTexto, larguraPagina - margem - 4, yTexto);
    yTexto += 6;
    const totalParcelas = dados.parcelas.reduce((soma, p) => soma + p.valor, 0);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(paleta.texto);
    pdf.text("Total parcelas:", colunas.valor - 32, yTexto, { align: "right" });
    pdf.text(formatarParaReais(totalParcelas), colunas.valor, yTexto, { align: "right" });
    yTexto += 7;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(paleta.texto);
    pdf.text(`Forma padrão: ${dados.formaPadrao}  •  Condição: ${dados.condicao}`, margem + 4, yTexto);
    return y + altura + 5;
};

const garantirEspaco = (pdf: jsPDF, y: number, alturaNecessaria: number): number => {
    if (y + alturaNecessaria > pdf.internal.pageSize.height - 20) {
        pdf.addPage();
        return 20;
    }
    return y;
};

interface ItemPix {
    titulo: string;
    detalhe: string;
    pix: PixFatura;
}

const itensPixDaFatura = (dados: DadosFatura): ItemPix[] => {
    if (dados.pixUnico && dados.pixUnico.copiaECola) {
        const emAberto = dados.parcelas.filter(p => p.emAberto);
        return [{
            titulo: `Valor em aberto: ${formatarParaReais(valorEmAbertoDaFatura(dados))}`,
            detalhe: emAberto.length > 1 ? `${emAberto.length} parcelas em aberto somadas em um único PIX` : "Pagamento integral da fatura",
            pix: dados.pixUnico,
        }];
    }
    return dados.parcelas
        .filter(p => p.pix && p.pix.copiaECola)
        .map(p => ({
            titulo: dados.parcelas.length > 1 ? `Parcela ${p.parcela}: ${formatarParaReais(p.valor)}` : `Valor: ${formatarParaReais(p.valor)}`,
            detalhe: textoData(p.vencimento) ? `Vencimento ${textoData(p.vencimento)}` : "",
            pix: p.pix!,
        }));
};

const blocoPix = (pdf: jsPDF, y: number, dados: DadosFatura): number => {
    const itens = itensPixDaFatura(dados);
    if (itens.length === 0) return y;

    const larguraPagina = pdf.internal.pageSize.width;
    const ladoQr = 46;
    const xTexto = margem + 4 + ladoQr + 6;
    const larguraTexto = larguraPagina - margem - 4 - xTexto;
    const alturaItem = ladoQr + 6;

    itens.forEach((item, indice) => {
        const altura = 12 + alturaItem + 2;
        y = garantirEspaco(pdf, y, altura);
        caixa(pdf, y, altura);
        tituloDaCaixa(pdf, margem + 4, y + 6, indice === 0 ? "Pague com PIX" : "Pague com PIX (continuação)");

        const yItem = y + 10;
        if (item.pix.imagem) {
            pdf.addImage(item.pix.imagem, "PNG", margem + 4, yItem, ladoQr, ladoQr);
        }

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(paleta.texto);
        pdf.text(item.titulo, xTexto, yItem + 7);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(paleta.apagado);
        if (item.detalhe) pdf.text(item.detalhe, xTexto, yItem + 13);
        pdf.text("Abra o aplicativo do seu banco, escolha pagar com PIX e aponte a câmera para o código ao lado.", xTexto, yItem + 19, { maxWidth: larguraTexto });

        pdf.setFontSize(7.5);
        pdf.setTextColor(paleta.apagado);
        pdf.text("Ou use o PIX copia e cola:", xTexto, yItem + 29);
        pdf.setFontSize(6.5);
        pdf.setTextColor(paleta.texto);
        const linhasCodigo = (pdf.splitTextToSize(item.pix.copiaECola, larguraTexto) as string[]).slice(0, 5);
        pdf.text(linhasCodigo, xTexto, yItem + 33);

        y += altura + 5;
    });
    return y;
};

const blocoTotal = (pdf: jsPDF, y: number, dados: DadosFatura): number => {
    const larguraPagina = pdf.internal.pageSize.width;
    const largura = 80;
    const x = larguraPagina - margem - largura;
    const altura = 22;
    pdf.setDrawColor(paleta.texto);
    pdf.setLineWidth(0.6);
    pdf.roundedRect(x, y, largura, altura, raio, raio, "S");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(paleta.texto);
    pdf.text(`Itens (${dados.itens.length})`, x + 4, y + 7);
    pdf.text(formatarParaReais(totalDaFatura(dados)), x + largura - 4, y + 7, { align: "right" });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(paleta.apagado);
    pdf.text("TOTAL DA FATURA", x + 4, y + 16);
    pdf.text(formatarParaReais(totalDaFatura(dados)), x + largura - 4, y + 16, { align: "right" });
    return y + altura + 8;
};

const rodape = (pdf: jsPDF, y: number, dados: DadosFatura) => {
    const larguraPagina = pdf.internal.pageSize.width;
    pdf.setDrawColor(paleta.texto);
    pdf.setLineWidth(0.6);
    pdf.line(margem, y, larguraPagina - margem, y);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(paleta.apagado);
    pdf.text(`Documento gerado eletronicamente por LocaSmartPro  •  Fatura Nº ${dados.numero}`, larguraPagina / 2, y + 6, { align: "center" });
};

export const desenharFatura = (pdf: jsPDF, dados: DadosFatura, logo: HTMLImageElement | null) => {
    let y = cabecalho(pdf, dados, logo);
    y = blocoCliente(pdf, y, dados.cliente);
    y = blocoItens(pdf, y, dados);
    y = blocoParcelas(pdf, y, dados);
    y = blocoPix(pdf, y, dados);
    y = garantirEspaco(pdf, y, 40);
    y = blocoTotal(pdf, y, dados);
    rodape(pdf, y, dados);
};

export const gerarPdfFaturas = (faturas: DadosFatura[], logo: HTMLImageElement | null): jsPDF => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    faturas.forEach((fatura, indice) => {
        if (indice > 0) pdf.addPage();
        desenharFatura(pdf, fatura, logo);
    });
    return pdf;
};
